import React, { useState, useRef, useEffect } from 'react';
import { ExternalLink, Github, BarChart3, MessageSquare, Eye, Database, AlertTriangle, CheckCircle, Zap, Globe, ArrowRight, Send, Loader2, Music } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';

const Projects = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { projects: projectsFromAPI } = useData();

  // API projects from admin panel
  const apiProjects = projectsFromAPI.data || [];

  // Chat state for Sagopa-Kumru demo
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Send message to backend chat proxy
  const sendMessage = async () => {
    if (!chatInput.trim() || isLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: "Sen Sagopa Kajmer'sin. Türk rap müziğinin efsanevi ismi, derin ve felsefi sözleriyle tanınan bir sanatçısın. Melankolik, şiirsel ve düşündürücü bir dille konuş. Kısa ve öz cevaplar ver. Rap tarzında, sokak ağzıyla karışık şiirsel ifadeler kullan."
            },
            {
              role: "user",
              content: userMessage
            }
          ],
          max_new_tokens: 256,
          temperature: 0.8,
          repetition_penalty: 1.2
        })
      });

      const data = await response.json();

      // RunPod async API - need to poll for result
      if (data.id) {
        const result = await pollForResult(data.id);
        setChatMessages(prev => [...prev, { role: 'assistant', content: result }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { role: 'assistant', content: t.projects.chatDemo?.error || 'Connection error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Poll backend for async result
  const pollForResult = async (jobId) => {
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const response = await fetch(`${API_URL}/chat/status/${jobId}`);

      const data = await response.json();

      if (data.status === 'COMPLETED') {
        // RunPod response structure: { output: { base_model, lora_adapter, output, usage } }
        const output = data.output;
        let text = '';
        if (typeof output === 'string') {
          text = output;
        } else if (output && typeof output === 'object') {
          text = output.output || output.text || output.response || output.generated_text || '';
        }

        // Clean up the response - remove special tokens and artifacts
        text = text
          .replace(/<\|[^>]*\|>/g, '') // Remove <|...|> tokens
          .replace(/<\|z>/g, '')
          .replace(/<\|im_start\|>/g, '')
          .replace(/<\|im_end\|>/g, '')
          .replace(/<\|user\|>/g, '')
          .replace(/<\|assistant\|>/g, '')
          .replace(/<\|system\|>/g, '')
          .replace(/\[INST\]|\[\/INST\]/g, '')
          .replace(/<<SYS>>|<\/SYS>>/g, '')
          .trim();

        // Take only the first coherent response (before it starts repeating or asking questions back)
        const lines = text.split('\n');
        let cleanResponse = [];
        for (const line of lines) {
          // Stop if we see patterns that suggest the model is confused
          if (line.includes('Naber?') && cleanResponse.length > 0) break;
          if (line.includes('<|') || line.includes('|>')) continue;
          cleanResponse.push(line);
          // Limit to reasonable length
          if (cleanResponse.join('\n').length > 500) break;
        }

        return cleanResponse.join('\n').trim() || 'Düşüncelerim derin, kelimeler yetersiz...';
      } else if (data.status === 'FAILED') {
        throw new Error('Job failed');
      }
    }
    throw new Error('Timeout');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const projects = [
    {
      id: 1,
      nameKey: 'project1',
      logo: <Music className="w-6 h-6" />,
      techStack: ["Python", "LoRA", "Transformers", "RunPod", "Docker", "React"],
      category: "NLP/LLM",
      status: "Live",
      demoUrl: null, // Interactive demo embedded
      githubUrl: "https://github.com/salihdede",
      bgColor: "bg-purple-600",
      bgColorLight: "bg-purple-500/10",
      isInteractive: true
    },
    {
      id: 2,
      nameKey: 'project2',
      logo: <Eye className="w-6 h-6" />,
      techStack: ["Python", "OpenCV", "YOLO v8", "FastAPI", "TensorRT", "CUDA"],
      category: "Computer Vision",
      status: "Production",
      demoUrl: "https://cv-demo.vercel.app",
      githubUrl: "https://github.com",
      bgColor: "bg-blue-500",
      bgColorLight: "bg-blue-500/10"
    },
    {
      id: 3,
      nameKey: 'project3',
      logo: <MessageSquare className="w-6 h-6" />,
      techStack: ["Python", "Transformers", "Node.js", "MongoDB", "Socket.io", "AWS"],
      category: "NLP",
      status: "10K+ Users",
      demoUrl: "https://chatbot-demo.vercel.app",
      githubUrl: "https://github.com",
      bgColor: "bg-blue-700",
      bgColorLight: "bg-blue-500/10"
    },
    {
      id: 4,
      nameKey: 'project4',
      logo: <BarChart3 className="w-6 h-6" />,
      techStack: ["React", "D3.js", "Node.js", "Redis", "ClickHouse", "WebSocket"],
      category: "Data Science",
      status: "2M+ Events/Day",
      demoUrl: "https://analytics-demo.vercel.app",
      githubUrl: "https://github.com",
      bgColor: "bg-gray-700",
      bgColorLight: "bg-gray-500/10"
    },
    {
      id: 5,
      nameKey: 'project5',
      logo: <Database className="w-6 h-6" />,
      techStack: ["Node.js", "Docker", "Kubernetes", "MongoDB", "RabbitMQ", "AWS"],
      category: "Backend",
      status: "Global Scale",
      demoUrl: "https://ecommerce-demo.vercel.app",
      githubUrl: "https://github.com",
      bgColor: "bg-gray-600",
      bgColorLight: "bg-gray-500/10"
    }
  ];

  return (
    <section id="projects" className={`py-24 relative overflow-hidden ${isDark ? 'bg-[#0a0f1a]' : 'bg-white'}`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 right-1/4 w-[500px] h-[500px] ${isDark ? 'bg-blue-500/5' : 'bg-blue-200/20'} rounded-full blur-[150px]`}></div>
        <div className={`absolute bottom-1/4 left-1/4 w-[400px] h-[400px] ${isDark ? 'bg-blue-500/5' : 'bg-blue-200/20'} rounded-full blur-[150px]`}></div>

        {/* Grid Pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: isDark
              ? `linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)`
              : `linear-gradient(rgba(148, 163, 184, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.15) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        ></div>

        {/* Horizontal Sparkles */}
        <div className={`projects-sparkle-h1 absolute w-20 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-blue-400' : 'via-blue-400'} to-transparent`} style={{ top: '15%' }}></div>
        <div className={`projects-sparkle-h2 absolute w-28 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-cyan-400' : 'via-cyan-400'} to-transparent`} style={{ top: '35%' }}></div>
        <div className={`projects-sparkle-h3 absolute w-24 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-blue-500' : 'via-blue-500'} to-transparent`} style={{ top: '55%' }}></div>
        <div className={`projects-sparkle-h4 absolute w-20 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-indigo-400' : 'via-indigo-400'} to-transparent`} style={{ top: '75%' }}></div>

        {/* Vertical Sparkles */}
        <div className={`projects-sparkle-v1 absolute w-[2px] h-20 bg-gradient-to-b from-transparent ${isDark ? 'via-blue-400' : 'via-blue-400'} to-transparent`} style={{ left: '10%' }}></div>
        <div className={`projects-sparkle-v2 absolute w-[2px] h-24 bg-gradient-to-b from-transparent ${isDark ? 'via-cyan-400' : 'via-cyan-400'} to-transparent`} style={{ left: '30%' }}></div>
        <div className={`projects-sparkle-v3 absolute w-[2px] h-28 bg-gradient-to-b from-transparent ${isDark ? 'via-blue-500' : 'via-blue-500'} to-transparent`} style={{ left: '70%' }}></div>
        <div className={`projects-sparkle-v4 absolute w-[2px] h-20 bg-gradient-to-b from-transparent ${isDark ? 'via-indigo-400' : 'via-indigo-400'} to-transparent`} style={{ left: '90%' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-20">
          <p className={`font-light tracking-widest uppercase mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t.projects.sectionTitle}</p>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-black mb-6 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {t.projects.title1}
            <span className="text-blue-500"> {t.projects.title2}</span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.projects.subtitle}
          </p>
        </div>

        {/* Projects List - Infinite Scroll Style */}
        <div className="space-y-24 lg:space-y-32">
          {projects.map((project, index) => {
            const projectData = t.projects[project.nameKey];
            return (
              <div
                key={project.id}
                className="relative"
              >
                {/* Project Number Badge */}
                <div className="absolute -left-4 lg:-left-16 top-0 hidden lg:block">
                  <span className={`text-8xl font-black ${isDark ? 'text-blue-500 opacity-20' : 'text-blue-400 opacity-30'}`}>
                    0{index + 1}
                  </span>
                </div>

                {/* Main Project Card */}
                <div className={`backdrop-blur-sm border rounded-3xl overflow-hidden transition-all duration-500 ${
                  isDark
                    ? `${project.bgColorLight} border-white/5 hover:border-white/10`
                    : 'bg-slate-50/80 border-slate-200 hover:border-blue-300 shadow-sm hover:shadow-lg'
                }`}>
                  <div className={`grid lg:grid-cols-5 gap-0 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>

                    {/* Sol Panel: Proje Bilgileri (2/5) */}
                    <div className={`lg:col-span-2 p-8 lg:p-10 border-b lg:border-b-0 ${index % 2 === 1 ? 'lg:col-start-4 lg:border-l' : 'lg:border-r'} ${isDark ? 'border-white/5' : 'border-gray-200'}`}>

                      {/* Proje Başlığı */}
                      <div className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-14 h-14 ${project.bgColor} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                            {project.logo}
                          </div>
                          <div>
                            <span className={`inline-block px-3 py-1 ${project.bgColor} text-white text-xs font-semibold rounded-full mb-1`}>
                              {project.category}
                            </span>
                            <div className="flex items-center gap-2 text-emerald-400 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              <span>{project.status}</span>
                            </div>
                          </div>
                        </div>
                        <h3 className={`text-2xl lg:text-3xl font-bold leading-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {projectData.name}
                        </h3>
                      </div>

                      {/* Problem & Solution */}
                      <div className="space-y-6 mb-8">
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-red-400" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-2">{t.projects.problem}</h4>
                            <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{projectData.problem}</p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                            <Zap className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-emerald-400 uppercase tracking-wide mb-2">{t.projects.solution}</h4>
                            <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{projectData.solution}</p>
                          </div>
                        </div>
                      </div>

                      {/* Tech Stack */}
                      <div className="mb-8">
                        <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-4">{t.projects.techStack}</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.techStack.map((tech, idx) => (
                            <span
                              key={idx}
                              className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                                isDark
                                  ? 'bg-white/5 border-white/10 text-gray-300 hover:border-blue-500/30 hover:text-white'
                                  : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:bg-blue-50'
                              }`}
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        {!project.isInteractive && project.demoUrl && (
                          <a
                            href={project.demoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`group flex items-center justify-center gap-2 px-6 py-3 ${project.bgColor} text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300`}
                          >
                            <Globe className="w-4 h-4" />
                            {t.projects.liveDemo}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </a>
                        )}
                        {project.isInteractive && (
                          <div className={`flex items-center gap-2 px-6 py-3 ${project.bgColor} text-white rounded-xl font-semibold`}>
                            <MessageSquare className="w-4 h-4" />
                            Interactive Demo →
                          </div>
                        )}
                        <a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center justify-center gap-2 px-6 py-3 border rounded-xl font-medium transition-all ${
                            isDark
                              ? 'bg-white/5 border-white/10 text-gray-300 hover:text-white hover:border-blue-500/30'
                              : 'bg-white border-slate-200 text-slate-600 hover:text-slate-800 hover:border-blue-400'
                          }`}
                        >
                          <Github className="w-4 h-4" />
                          {t.projects.sourceCode}
                        </a>
                      </div>
                    </div>

                    {/* Sağ Panel: Demo Alanı (3/5) */}
                    <div className={`lg:col-span-3 p-6 lg:p-8 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>

                      {/* Browser Window */}
                      <div className={`rounded-2xl overflow-hidden border h-full ${isDark ? 'bg-[#0d1117] border-white/5 shadow-2xl' : 'bg-slate-100 border-slate-200 shadow-lg'}`}>
                        {/* Browser Header */}
                        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'bg-[#161b22] border-white/5' : 'bg-slate-200 border-slate-300'}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          </div>
                          <div className="flex-1 mx-4">
                            <div className={`rounded-lg px-4 py-1.5 text-sm font-mono truncate ${isDark ? 'bg-[#0d1117] text-gray-500' : 'bg-white text-slate-500'}`}>
                              {project.isInteractive ? 'sagopa-kumru-chatbot.ai' : project.demoUrl}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400 text-sm">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                            <span>Live</span>
                          </div>
                        </div>

                        {/* Demo Content */}
                        <div className={`${project.isInteractive ? 'h-[500px]' : 'aspect-[16/10]'} p-4 lg:p-6 relative overflow-hidden ${isDark ? 'bg-[#0d1117]' : 'bg-slate-50'}`}>
                          {project.isInteractive ? (
                            /* Interactive Chat Demo for Sagopa-Kumru */
                            <div className="relative z-10 h-full flex flex-col justify-between">
                              {/* Chat Messages Area */}
                              <div
                                ref={chatContainerRef}
                                className={`flex-1 overflow-y-auto space-y-3 pr-2 ${isDark ? 'scrollbar-dark' : 'scrollbar-light'}`}
                              >
                                {/* Welcome Message */}
                                {chatMessages.length === 0 && (
                                  <div className={`flex gap-3 items-start`}>
                                    <div className={`w-8 h-8 rounded-full ${project.bgColor} flex items-center justify-center flex-shrink-0`}>
                                      <Music className="w-4 h-4 text-white" />
                                    </div>
                                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${isDark ? 'bg-purple-500/20 text-gray-200' : 'bg-purple-100 text-gray-700'}`}>
                                      <p className="text-sm">{t.projects.chatDemo?.welcome || "Selam ben Sago ruhlu bir chatbotum. Sor gelsin!"}</p>
                                    </div>
                                  </div>
                                )}

                                {/* Chat Messages */}
                                {chatMessages.map((msg, idx) => (
                                  <div key={idx} className={`flex gap-3 items-start ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                      msg.role === 'user'
                                        ? (isDark ? 'bg-blue-500' : 'bg-blue-500')
                                        : project.bgColor
                                    }`}>
                                      {msg.role === 'user' ? (
                                        <span className="text-white text-xs font-bold">U</span>
                                      ) : (
                                        <Music className="w-4 h-4 text-white" />
                                      )}
                                    </div>
                                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                                      msg.role === 'user'
                                        ? (isDark ? 'bg-blue-500/20 text-gray-200' : 'bg-blue-100 text-gray-700')
                                        : (isDark ? 'bg-purple-500/20 text-gray-200' : 'bg-purple-100 text-gray-700')
                                    }`}>
                                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                    </div>
                                  </div>
                                ))}

                                {/* Loading Indicator */}
                                {isLoading && (
                                  <div className="flex gap-3 items-start">
                                    <div className={`w-8 h-8 rounded-full ${project.bgColor} flex items-center justify-center flex-shrink-0`}>
                                      <Music className="w-4 h-4 text-white" />
                                    </div>
                                    <div className={`px-4 py-3 rounded-2xl ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                                      <div className="flex items-center gap-2">
                                        <Loader2 className={`w-4 h-4 animate-spin ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {t.projects.chatDemo?.thinking || 'Düşünüyor...'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Chat Input - Fixed at bottom */}
                              <div className={`mt-4 flex gap-2 items-center p-2 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
                                <input
                                  type="text"
                                  value={chatInput}
                                  onChange={(e) => setChatInput(e.target.value)}
                                  onKeyDown={handleKeyPress}
                                  placeholder={t.projects.chatDemo?.placeholder || "Sagopa veya Kumru'ya bir şey yaz..."}
                                  className={`flex-1 px-3 py-2 bg-transparent outline-none text-sm ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'}`}
                                  disabled={isLoading}
                                />
                                <button
                                  onClick={sendMessage}
                                  disabled={isLoading || !chatInput.trim()}
                                  className={`p-2 rounded-lg transition-all ${
                                    isLoading || !chatInput.trim()
                                      ? (isDark ? 'bg-white/5 text-gray-500' : 'bg-slate-100 text-slate-400')
                                      : `${project.bgColor} text-white hover:opacity-90`
                                  }`}
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Standard Project Mockup */
                            <>
                              {/* Decorative Grid */}
                              <div className={`absolute inset-0 ${isDark ? 'opacity-5' : 'opacity-10'}`}
                                style={{
                                  backgroundImage: `linear-gradient(${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px)`,
                                  backgroundSize: '40px 40px'
                                }}
                              ></div>

                              {/* Project Visual Representation */}
                              <div className="relative z-10 h-full flex flex-col">
                                {/* Top Bar */}
                                <div className="flex items-center justify-between mb-4 lg:mb-6">
                                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 ${project.bgColor} rounded-lg`}>
                                    {project.logo}
                                    <span className="text-white font-semibold text-sm lg:text-base">{projectData.name.split(' ')[0]}</span>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className={`w-16 lg:w-24 h-6 lg:h-8 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                    <div className={`w-12 lg:w-20 h-6 lg:h-8 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                  </div>
                                </div>

                                {/* Main Content Area */}
                                <div className="flex-1 grid grid-cols-3 gap-3 lg:gap-4">
                                  {/* Sidebar */}
                                  <div className="space-y-2 lg:space-y-3">
                                    <div className={`h-8 lg:h-10 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                    <div className={`h-6 lg:h-8 rounded-lg w-4/5 ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                    <div className={`h-6 lg:h-8 rounded-lg w-3/5 ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                    <div className={`h-6 lg:h-8 rounded-lg w-4/5 ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                    <div className={`h-8 lg:h-10 ${project.bgColor} rounded-lg opacity-60`}></div>
                                  </div>

                                  {/* Main Area */}
                                  <div className="col-span-2 space-y-3 lg:space-y-4">
                                    <div className={`h-8 lg:h-12 rounded-lg w-2/3 ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                    <div className="grid grid-cols-2 gap-2 lg:gap-3">
                                      <div className={`aspect-video rounded-xl border ${isDark ? `${project.bgColorLight} border-white/10` : 'bg-blue-50 border-blue-200'}`}></div>
                                      <div className={`aspect-video rounded-xl border ${isDark ? 'bg-white/5 border-white/5' : 'bg-slate-100 border-slate-200'}`}></div>
                                    </div>
                                    <div className={`h-12 lg:h-16 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}></div>
                                    <div className="flex gap-2 lg:gap-3">
                                      <div className={`h-8 lg:h-10 w-20 lg:w-28 ${project.bgColor} rounded-lg`}></div>
                                      <div className={`h-8 lg:h-10 w-16 lg:w-24 rounded-lg ${isDark ? 'bg-white/10' : 'bg-slate-300'}`}></div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Hover Overlay */}
                              {project.demoUrl && (
                                <a
                                  href={project.demoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-20"
                                >
                                  <div className={`flex items-center gap-3 px-6 lg:px-8 py-3 lg:py-4 ${project.bgColor} rounded-xl text-white font-semibold shadow-2xl transform hover:scale-105 transition-transform`}>
                                    <ExternalLink className="w-5 h-5" />
                                    {t.projects.openLiveDemo}
                                  </div>
                                </a>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connector Line to Next Project */}
                {index < projects.length - 1 && (
                  <div className="flex justify-center py-8">
                    <div className={`w-px h-16 ${isDark ? 'bg-blue-500/50' : 'bg-blue-300'}`}></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* API Projects from Admin Panel */}
        {apiProjects.length > 0 && (
          <div className="mt-24">
            <div className="text-center mb-12">
              <h3 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {t.projects.moreProjects || 'More Projects'}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {apiProjects.map((project) => {
                const title = typeof project.title === 'object' ? (project.title[language] || project.title.en || project.title.tr) : project.title;
                const description = typeof project.description === 'object' ? (project.description[language] || project.description.en || project.description.tr) : project.description;
                const technologies = Array.isArray(project.technologies) ? project.technologies : [];

                return (
                  <div
                    key={project.id}
                    className={`rounded-2xl border overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
                      isDark
                        ? 'bg-white/5 border-white/5 hover:border-blue-500/30'
                        : 'bg-slate-50 border-slate-200 hover:border-blue-400 shadow-sm hover:shadow-lg'
                    }`}
                  >
                    {project.image && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={project.image}
                          alt={title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h4 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>{title}</h4>
                      {description && (
                        <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{description}</p>
                      )}
                      {technologies.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {technologies.slice(0, 5).map((tech, idx) => (
                            <span
                              key={idx}
                              className={`px-2 py-1 text-xs rounded-lg ${
                                isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'
                              }`}
                            >
                              {tech}
                            </span>
                          ))}
                          {technologies.length > 5 && (
                            <span className={`px-2 py-1 text-xs rounded-lg ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-slate-200 text-slate-500'}`}>
                              +{technologies.length - 5}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex gap-3">
                        {project.live && (
                          <a href={project.live} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition">
                            <Globe className="w-4 h-4" /> {t.projects.liveDemo || 'Live Demo'}
                          </a>
                        )}
                        {project.github && (
                          <a href={project.github} target="_blank" rel="noopener noreferrer"
                            className={`flex items-center gap-1 text-sm transition ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-800'}`}>
                            <Github className="w-4 h-4" /> {t.projects.sourceCode || 'Source'}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <div className={`inline-flex flex-col items-center gap-4 p-8 border rounded-3xl ${
            isDark
              ? 'bg-white/[0.02] border-white/5'
              : 'bg-slate-50 border-slate-200'
          }`}>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.projects.viewMore}</p>
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all"
            >
              <Github className="w-5 h-5" />
              {t.projects.viewAllOnGithub}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
